import React, { useState, useContext, useEffect, useRef } from "react";
import { Eye, EyeOff, ArrowRight, Camera, Home, Briefcase, FileText, Search, Flame, Wallet, Clock, Star, ShieldCheck } from "lucide-react";
import { AuthContext } from "../../App";
import { PasswordRecoveryScreen } from "../../pages/auth/PasswordRecoveryScreen";
import { PrivacyPolicy } from "../../pages/legal/PrivacyPolicy";
import { TermsOfService } from "../../pages/legal/TermsOfService";
import { SocialProofCounter } from "../../components/SocialProofCounter";
import { ExecutorsProofBadge } from "../../components/ExecutorsProofBadge";
import { LeadForm } from "../../components/LeadForm";
import { SiteFooter } from "../../components/SiteFooter";
import { formatRuPhone, isValidRuPhone, RU_PHONE_PLACEHOLDER } from "../../utils/phone";

export const AuthScreen = ({ setCurrentView, currentView }) => {
  const { login, register } = React.useContext(AuthContext);
  const [isLogin, setIsLogin] = useState(true);
  const [executorMode, setExecutorMode] = useState(false);
  const [showPasswordRecovery, setShowPasswordRecovery] = useState(false);
  const [initialRecoveryToken, setInitialRecoveryToken] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    role: "customer",
    phone: ""
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Состояния для согласий 152-ФЗ
  const [consentData, setConsentData] = useState({
    personalDataProcessing: false,
    termsOfService: false,
    privacyPolicy: false,
    marketingEmails: false, // необязательное согласие
    ageConfirmation: false  // подтверждение совершеннолетия (пункт аудита #11)
  });

  // Состояния для модальных окон документов
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // Обработка ссылки восстановления пароля из письма (/reset-password?token=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const resetToken = params.get('token');
    if (window.location.pathname === '/reset-password' && resetToken) {
      setInitialRecoveryToken(resetToken);
      setShowPasswordRecovery(true);
      // Чистим URL чтобы токен не остался в адресной строке
      window.history.replaceState({}, '', '/');
    }
  }, []);

  // Диплинк с SEO-посадочных страниц (?register=1&role=executor|customer):
  // сразу открываем вкладку регистрации с нужной ролью.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('register') === '1') {
      setIsLogin(false);
      const role = params.get('role');
      if (role === 'executor' || role === 'customer') {
        setFormData(prev => ({ ...prev, role }));
      }
      if (role === 'executor') {
        setExecutorMode(true);
      }
      window.history.replaceState({}, '', '/');
    }
  }, []);

  // Карусель историй клиентов — хуки должны быть ДО условного return
  const customerStories = [
    {
      text: "Попросил сфотографировать двор детства в Казани — через 2 часа получил 20 фото. Сердце сжалось от нахлынувших воспоминаний.",
      author: "Дмитрий, Москва",
    },
    {
      text: "Искали тихий пляж для отпуска — в интернете красивые фото, но какова обстановка сейчас? Разместили заказ на Реверсии. Местный житель снял видео прямо с берега: малолюдно, чисто, вода прозрачная. Забронировали в тот же день. Отдых превзошёл ожидания.",
      author: "Семья Ковалевых, Екатеринбург",
    },
    {
      text: "Уехал из России восемь лет назад. Мама похоронена в Саратове — добраться не могу. Через Реверсию нашёл человека который ухаживает за могилой каждый месяц. Присылает фото, рассказывает как там всё выглядит. Я знаю что она не забыта. Для меня это не просто услуга — это способ оставаться рядом несмотря на расстояние.",
      author: "Андрей, Берлин",
    },
    {
      text: "Поступила информация о крупном ДТП на трассе в 200 км от редакции. Штатный корреспондент в другом районе, командировка — минимум три часа. Разместил срочный заказ на Реверсии. Через 18 минут верифицированный мобильный корреспондент уже был на месте — прислал фото, видео и короткий текст с опросом очевидцев. Материал вышел в эфир раньше федеральных каналов.",
      author: "Редактор регионального новостного издания",
    },
    {
      text: "Потекла крыша над верандой на даче после сильных дождей. Местные мастера либо не брались за маленький объём, либо просили ждать неделю. Разместил задание на Реверсии вечером — утром откликнулся кровельщик из соседнего посёлка. Через день крыша была готова, он прислал подробный фотоотчёт. Качество отличное, цена разумная.",
      author: "Игорь, Подмосковье",
    },
    {
      text: "Заглохла машина на трассе в 120 км от Воронежа. Эвакуатор обещал три часа ожидания. Открыл Реверсию, разместил задание: авторемонт с выездом. Через 25 минут приехал местный механик — нашёл неисправность топливного насоса, договорились по запчасти на месте. К вечеру был дома. Обошлось в разы дешевле эвакуации.",
      author: "Василий, Воронеж",
    },
  ];
  const executorStories = [
    {
      text: "Первое задание взял по дороге с работы — сфотографировать фасад дома для заказчика из другого города. Двадцать минут — и 800 рублей на карте. Теперь беру два-три задания в неделю.",
      author: "Сергей, исполнитель",
    },
    {
      text: "Совмещаю с учёбой: доставка, фотоотчёты, мелкие поручения. Заказчик резервирует деньги заранее, поэтому за оплату я спокойна — после подтверждения работы они сразу мои.",
      author: "Анна, студентка",
    },
    {
      text: "Ухаживаю за могилами близких по заказам людей, которые живут далеко. Для них это очень важно, а для меня — стабильная и осмысленная подработка рядом с домом.",
      author: "Ольга, исполнитель",
    },
  ];
  const stories = executorMode ? executorStories : customerStories;
  const [activeSlide, setActiveSlide] = useState(0);
  const slideTimerRef = useRef(null);

  const goToSlide = (idx) => {
    setActiveSlide(idx);
    // Сбрасываем автоплей при ручном переключении
    if (slideTimerRef.current) clearInterval(slideTimerRef.current);
    slideTimerRef.current = setInterval(() => {
      setActiveSlide(prev => (prev + 1) % stories.length);
    }, 11000);
  };

  useEffect(() => {
    setActiveSlide(0);
    if (slideTimerRef.current) clearInterval(slideTimerRef.current);
    slideTimerRef.current = setInterval(() => {
      setActiveSlide(prev => (prev + 1) % (executorMode ? 3 : customerStories.length));
    }, 11000);
    return () => clearInterval(slideTimerRef.current);
  }, [executorMode]);

  // Функции для открытия документов
  const openPrivacyPolicy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPrivacyModal(true);
  };

  const openTermsOfService = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowTermsModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (isLogin) {
        const result = await login(formData.email.trim().toLowerCase(), formData.password);
        if (!result.success) {
          setError(result.error);
        } else {
          if (typeof window !== 'undefined' && window.ym) {
            window.ym(109617150, 'reachGoal', 'login');
          }
        }
      } else {
        if ((formData.password || "").length < 8) {
          setError("Пароль должен содержать минимум 8 символов");
          setLoading(false);
          return;
        }
        // Валидация телефона при регистрации
        if (!formData.phone || formData.phone.trim() === '') {
          setError("Номер телефона обязателен для регистрации");
          setLoading(false);
          return;
        }
        
        // Простая валидация формата телефона
        const phoneRegex = /^[\+]?[0-9\s\-\(\)]{10,20}$/;
        if (!phoneRegex.test(formData.phone.trim()) || !isValidRuPhone(formData.phone)) {
          setError("Введите корректный номер телефона в формате +7 (999) 123-45-67");
          setLoading(false);
          return;
        }
        
        // Валидация обязательных согласий 152-ФЗ
        if (!consentData.personalDataProcessing) {
          setError("Необходимо дать согласие на обработку персональных данных");
          setLoading(false);
          return;
        }
        
        if (!consentData.termsOfService) {
          setError("Необходимо согласиться с условиями использования");
          setLoading(false);
          return;
        }
        
        if (!consentData.privacyPolicy) {
          setError("Необходимо согласиться с политикой конфиденциальности");
          setLoading(false);
          return;
        }

        if (!consentData.ageConfirmation) {
          setError("Необходимо подтвердить, что вам исполнилось 18 лет");
          setLoading(false);
          return;
        }
        
        // Источник регистрации (UTM-метки, захваченные при заходе на сайт)
        let regUtm = null;
        try { regUtm = JSON.parse(localStorage.getItem('rv_reg_utm') || 'null'); } catch (err) { regUtm = null; }

        // Объединяем данные формы с согласиями
        const registrationData = {
          ...formData,
          email: formData.email.trim().toLowerCase(),
          ...(regUtm && typeof regUtm === 'object' ? { utm: regUtm } : {}),
          consents: {
            personalDataProcessing: consentData.personalDataProcessing,
            termsOfService: consentData.termsOfService,
            privacyPolicy: consentData.privacyPolicy,
            marketingEmails: consentData.marketingEmails,
            ageConfirmation: consentData.ageConfirmation,
            consentDate: new Date().toISOString(),
            ipAddress: window.location.hostname // для логирования
          }
        };
        
        const result = await register(registrationData);
        if (result.success) {
          if (typeof window !== 'undefined' && window.ym) {
            window.ym(109617150, 'reachGoal', 'registration');
          }
          try { localStorage.removeItem('rv_reg_utm'); } catch (err) {}
          alert("Регистрация успешна! Мы отправили на ваш email письмо со ссылкой для подтверждения адреса. Теперь войдите в систему.");
          setIsLogin(true);
        } else {
          setError(result.error);
        }
      }
    } catch (error) {
      setError("Произошла ошибка");
    } finally {
      setLoading(false);
    }
  };

  if (showPasswordRecovery) {
    return (
      <PasswordRecoveryScreen
        onBack={() => { setShowPasswordRecovery(false); setInitialRecoveryToken(null); }}
        initialToken={initialRecoveryToken}
      />
    );
  }

  const inputClass = "w-full px-4 py-2.5 rounded-lg text-sm border transition-all";
  const inputStyle = {
    border: '1px solid var(--rv-border)',
    background: 'white',
    color: 'var(--rv-text-primary)',
    fontFamily: 'IBM Plex Sans, sans-serif',
  };

  return (
    <>
    <div className="min-h-screen flex" style={{background: 'var(--rv-paper)'}}>
      {/* Left — dark brand panel */}
      <div
        className="hidden lg:flex flex-col justify-between w-5/12 p-16"
        style={{background: 'var(--rv-navy)', color: 'white', flexShrink: 0}}
      >
        <div>
          <div className="flex items-center gap-3 mb-12">
            <img src="/images/logo.png" alt="Реверсия" className="w-10 h-10 rounded-lg object-contain"
              style={{background:'rgba(255,255,255,0.1)', padding:'4px'}}
              onError={e=>{e.target.style.display='none';}} />
            <span className="text-xl font-bold" style={{fontFamily:'Manrope,sans-serif', letterSpacing:'-0.01em'}}>Реверсия</span>
          </div>
          <h1 className="text-3xl font-bold leading-tight mb-5" style={{fontFamily:'Manrope,sans-serif', letterSpacing:'-0.02em', color:'white'}}>
            {executorMode ? (<>Зарабатывайте<br/>на заданиях рядом</>) : (<>Маркетплейс<br/>заданий на местах</>)}
          </h1>
          <p style={{color:'rgba(255,255,255,0.6)', lineHeight:'1.7', fontSize:'0.9375rem', marginBottom:'1.5rem'}}>
            {executorMode
              ? 'Выполняйте задания заказчиков — фото, видео, доставка, проверки, помощь по делу — и получайте оплату на карту.'
              : 'Заказчики публикуют задания — фото, видео, аудиты, тайный покупатель. Исполнители выполняют и получают оплату.'}
          </p>
          <ExecutorsProofBadge dark className="mb-6" />

          {/* Блок категорий / заработка */}
          <div style={{marginBottom: '1.75rem'}}>
            {executorMode ? (
              <>
                <p style={{color:'rgba(255,255,255,0.3)', fontSize:'0.6875rem', fontWeight:'700', letterSpacing:'0.09em', textTransform:'uppercase', marginBottom:'10px', fontFamily:'Manrope, sans-serif'}}>
                  Сколько можно заработать
                </p>
                <div style={{display:'grid', gridTemplateColumns:'1fr', gap:'6px'}} data-testid="auth-executor-earnings-block">
                  {[
                    { Icon: Clock,       label: 'Среднее задание — 1 200 ₽ за 30 минут' },
                    { Icon: Wallet,      label: 'Активные исполнители — 15 000 ₽ в месяц' },
                    { Icon: Star,        label: 'Топ исполнители — до 35 000 ₽ в месяц' },
                    { Icon: ShieldCheck, label: 'Оплата защищена эскроу — деньги резервируются заранее' },
                  ].map(({ Icon, label }) => (
                    <div key={label} style={{display:'flex', alignItems:'center', gap:'7px', padding:'6px 8px', borderRadius:'6px', background:'rgba(255,255,255,0.05)'}}>
                      <Icon size={13} style={{color:'var(--rv-teal)', flexShrink:0}} />
                      <span style={{color:'rgba(255,255,255,0.65)', fontSize:'0.75rem', fontFamily:'IBM Plex Sans, sans-serif'}}>{label}</span>
                    </div>
                  ))}
                </div>
                <p style={{color:'rgba(255,255,255,0.22)', fontSize:'0.6875rem', marginTop:'8px', fontFamily:'IBM Plex Sans, sans-serif'}}>
                  гибкий график — берите задания, когда удобно
                </p>
              </>
            ) : (
              <>
                <p style={{color:'rgba(255,255,255,0.3)', fontSize:'0.6875rem', fontWeight:'700', letterSpacing:'0.09em', textTransform:'uppercase', marginBottom:'10px', fontFamily:'Manrope, sans-serif'}}>
                  Что можно заказать
                </p>
                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px'}}>
                  {[
                    { Icon: Camera,    label: 'Фото и видео' },
                    { Icon: Home,      label: 'Помощь по дому' },
                    { Icon: Briefcase, label: 'Ремонт и мастера' },
                    { Icon: FileText,  label: 'Документы и SEO' },
                    { Icon: Search,    label: 'Тайная проверка' },
                    { Icon: Flame,     label: 'Мемориальный сервис' },
                  ].map(({ Icon, label }) => (
                    <div key={label} style={{display:'flex', alignItems:'center', gap:'7px', padding:'6px 8px', borderRadius:'6px', background:'rgba(255,255,255,0.05)'}}>
                      <Icon size={13} style={{color:'var(--rv-teal)', flexShrink:0}} />
                      <span style={{color:'rgba(255,255,255,0.65)', fontSize:'0.75rem', fontFamily:'IBM Plex Sans, sans-serif'}}>{label}</span>
                    </div>
                  ))}
                </div>
                <p style={{color:'rgba(255,255,255,0.22)', fontSize:'0.6875rem', marginTop:'8px', fontFamily:'IBM Plex Sans, sans-serif'}}>
                  и многое другое в разделе Общие услуги
                </p>
              </>
            )}
          </div>

          <div className="space-y-3">
            {/* Карусель историй клиентов */}
            <div
              key={activeSlide}
              className="story-slide-enter p-5 rounded-xl"
              data-testid="auth-story-slide"
              style={{background: 'rgba(255,255,255,0.06)', minHeight: '180px'}}
            >
              <div
                style={{
                  color: 'var(--rv-teal)',
                  fontSize: '2rem',
                  lineHeight: '1',
                  marginBottom: '10px',
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  fontWeight: '700',
                  display: 'block',
                  opacity: 0.9,
                }}
              >
                &ldquo;
              </div>
              <p
                style={{
                  color: 'rgba(255,255,255,0.88)',
                  lineHeight: '1.7',
                  fontSize: '0.875rem',
                  fontStyle: 'italic',
                  fontFamily: 'IBM Plex Sans, sans-serif',
                  margin: 0,
                }}
              >
                {stories[activeSlide].text}
              </p>
              <p
                style={{
                  color: 'rgba(255,255,255,0.45)',
                  fontSize: '0.75rem',
                  marginTop: '14px',
                  fontWeight: '600',
                  letterSpacing: '0.01em',
                  fontFamily: 'Manrope, sans-serif',
                }}
              >
                &mdash;&nbsp;{stories[activeSlide].author}
              </p>
              {(executorMode || activeSlide === 2) ? (
                <button
                  data-testid="become-executor-btn"
                  type="button"
                  onClick={() => {
                    setIsLogin(false);
                    setFormData(prev => ({ ...prev, role: 'executor' }));
                  }}
                  style={{
                    marginTop: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'var(--rv-teal)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '9px 16px',
                    fontSize: '0.8125rem',
                    fontWeight: '600',
                    fontFamily: 'IBM Plex Sans, sans-serif',
                    cursor: 'pointer',
                    transition: 'opacity 0.2s ease',
                    width: '100%',
                    justifyContent: 'space-between',
                  }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >
                  <span>Стать исполнителем</span>
                  <ArrowRight size={15} />
                </button>
              ) : (
                <button
                  data-testid="place-task-btn"
                  type="button"
                  onClick={() => {
                    setIsLogin(false);
                    setFormData(prev => ({ ...prev, role: 'customer' }));
                  }}
                  style={{
                    marginTop: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'rgba(255,255,255,0.12)',
                    color: 'white',
                    border: '1px solid rgba(255,255,255,0.22)',
                    borderRadius: '8px',
                    padding: '9px 16px',
                    fontSize: '0.8125rem',
                    fontWeight: '600',
                    fontFamily: 'IBM Plex Sans, sans-serif',
                    cursor: 'pointer',
                    transition: 'background 0.2s ease',
                    width: '100%',
                    justifyContent: 'space-between',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
                >
                  <span>Разместить задание</span>
                  <ArrowRight size={15} />
                </button>
              )}
            </div>
          </div>
        </div>

        <div>
          <SocialProofCounter variant="dark" className="mb-7" />
          <div className="flex items-center gap-2 mb-3">
            {stories.map((_, i) => (
              <button
                key={i}
                data-testid={`auth-story-dot-${i}`}
                onClick={() => goToSlide(i)}
                aria-label={`История ${i + 1}`}
                style={{
                  width: i === activeSlide ? '22px' : '8px',
                  height: '8px',
                  borderRadius: '4px',
                  background: i === activeSlide ? 'var(--rv-teal)' : 'rgba(255,255,255,0.3)',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  transition: 'width 0.3s ease, background 0.3s ease',
                  flexShrink: 0,
                }}
              />
            ))}
          </div>
          <p style={{color:'rgba(255,255,255,0.3)', fontSize:'0.8125rem'}}>
            reversia.ru — безопасные платежи через эскроу
          </p>
        </div>
      </div>

      {/* Right — form panel. pb на мобильном — чтобы cookie-баннер не перекрывал кнопку отправки */}
      <div className="flex flex-col justify-center items-center flex-1 px-6 pt-10 pb-40 lg:pb-10 overflow-y-auto">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-2 mb-8 self-start">
          <img src="/images/logo.png" alt="Реверсия" className="w-8 h-8 rounded object-contain"
            style={{background:'var(--rv-teal)', padding:'2px'}}
            onError={e=>{e.target.style.display='none';}} />
          <span className="text-xl font-bold" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-teal)'}}>Реверсия</span>
        </div>

        {/* Social proof — mobile only (desktop shows it in the navy panel) */}
        <div className="lg:hidden w-full max-w-sm mb-8 self-start">
          <SocialProofCounter variant="light" />
        </div>

        <div className="w-full max-w-sm">
          {/* Tabs */}
          <div className="flex rounded-xl overflow-hidden mb-8" style={{border:'1px solid var(--rv-border)', background:'white'}}>
            <button
              data-testid="auth-login-tab"
              onClick={() => setIsLogin(true)}
              className="flex-1 py-2.5 text-sm font-semibold transition-colors"
              style={{
                background: isLogin ? 'var(--rv-navy)' : 'white',
                color: isLogin ? 'white' : 'var(--rv-text-secondary)',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'IBM Plex Sans, sans-serif',
              }}
            >
              Войти
            </button>
            <button
              data-testid="auth-register-tab"
              onClick={() => setIsLogin(false)}
              className="flex-1 py-2.5 text-sm font-semibold transition-colors"
              style={{
                background: !isLogin ? 'var(--rv-navy)' : 'white',
                color: !isLogin ? 'white' : 'var(--rv-text-secondary)',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'IBM Plex Sans, sans-serif',
              }}
            >
              Регистрация
            </button>
          </div>

          <h2 className="text-2xl font-bold mb-1" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-navy)'}}>
            {isLogin ? 'С возвращением' : 'Создать аккаунт'}
          </h2>
          <p className="text-sm mb-6" style={{color:'var(--rv-text-secondary)'}}>
            {isLogin
              ? 'Введите данные для входа'
              : (formData.role === 'executor' ? '2 минуты — и вы видите задания рядом' : 'Заполните данные для регистрации')}
          </p>

          {/* Мобильный блок доверия — на десктопе это делает левая тёмная панель */}
          {!isLogin && (
            <div className="lg:hidden mb-6" data-testid="auth-mobile-trust-block">
              <div
                className="flex items-start gap-2.5 p-3 rounded-lg"
                style={{background:'var(--rv-white)', border:'1px solid var(--rv-border)', borderLeft:'3px solid var(--rv-teal)'}}
              >
                <ShieldCheck size={16} style={{color:'var(--rv-teal)', flexShrink:0, marginTop:'2px'}} />
                <p className="text-xs leading-relaxed m-0" style={{color:'var(--rv-text-secondary)', fontFamily:'IBM Plex Sans, sans-serif'}}>
                  {formData.role === 'executor'
                    ? 'Оплата защищена эскроу — деньги резервируются до результата. Выплата на карту после каждого задания.'
                    : 'Оплата защищена эскроу — деньги резервируются на платформе и уходят исполнителю только после вашего подтверждения.'}
                </p>
              </div>
              <p className="text-xs mt-2" style={{color:'var(--rv-text-muted)', fontFamily:'IBM Plex Sans, sans-serif'}}>
                {formData.role === 'executor'
                  ? 'Первые исполнители уже берут задания в ФМР, Юбилейном, Гидрострое, Энке и центре.'
                  : 'Исполнители работают в ФМР, Юбилейном, Гидрострое, Энке и центре Краснодара.'}
              </p>
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 rounded-lg text-sm" style={{background:'#FEF2F2', border:'1px solid #FECACA', color:'var(--rv-error)', fontFamily:'IBM Plex Sans,sans-serif'}}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{color:'var(--rv-text-primary)'}}>
                Email
              </label>
              <input
                data-testid="auth-email-input"
                type="email"
                required
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="email"
                spellCheck={false}
                inputMode="email"
                className={inputClass}
                style={inputStyle}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="example@mail.ru"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{color:'var(--rv-text-primary)'}}>
                Пароль
              </label>
              <div className="relative">
                <input
                  data-testid="auth-password-input"
                  type={showPassword ? "text" : "password"}
                  required
                  className={inputClass}
                  style={{...inputStyle, paddingRight:'2.5rem'}}
                  minLength={isLogin ? undefined : 8}
                  aria-describedby={isLogin ? undefined : "auth-password-hint"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={isLogin ? "Введите пароль" : "Минимум 8 символов"}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  style={{background:'none', border:'none', cursor:'pointer', color:'var(--rv-text-muted)'}}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {!isLogin && (
                <p id="auth-password-hint" data-testid="auth-password-hint" className="mt-1 text-xs"
                  style={{color: formData.password && formData.password.length < 8 ? 'var(--rv-danger, #b91c1c)' : 'var(--rv-text-muted)'}}>
                  Минимум 8 символов{formData.password ? ` (сейчас ${formData.password.length})` : ''}
                </p>
              )}
            </div>

            {/* Registration fields */}
            {!isLogin && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{color:'var(--rv-text-primary)'}}>
                    Имя
                  </label>
                  <input
                    data-testid="auth-name-input"
                    type="text"
                    required
                    className={inputClass}
                    style={inputStyle}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ваше имя"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{color:'var(--rv-text-primary)'}}>
                    Роль
                  </label>
                  <select
                    data-testid="auth-role-select"
                    className={inputClass}
                    style={inputStyle}
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  >
                    <option value="customer">Заказчик</option>
                    <option value="executor">Исполнитель</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{color:'var(--rv-text-primary)'}}>
                    Телефон <span style={{color:'var(--rv-error)'}}>*</span>
                  </label>
                  <input
                    data-testid="auth-phone-input"
                    type="tel"
                    required
                    className={inputClass}
                    style={inputStyle}
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: formatRuPhone(e.target.value) })}
                    placeholder={RU_PHONE_PLACEHOLDER}
                    inputMode="tel"
                    maxLength={18}
                  />
                  <p className="text-xs mt-1" style={{color:'var(--rv-text-muted)'}}>
                    Необходим для выплат исполнителям
                  </p>
                </div>

                {/* Согласия 152-ФЗ */}
                <div className="space-y-3 p-4 rounded-xl" style={{background:'var(--rv-paper)', border:'1px solid var(--rv-border)'}}>
                  <h4 className="text-sm font-semibold" style={{color:'var(--rv-text-primary)'}}>
                    Согласия (152-ФЗ)
                  </h4>

                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      required
                      className="mt-0.5 h-4 w-4 rounded"
                      style={{accentColor:'var(--rv-teal)'}}
                      checked={consentData.personalDataProcessing}
                      onChange={(e) => setConsentData({...consentData, personalDataProcessing: e.target.checked})}
                    />
                    <span className="text-xs leading-relaxed" style={{color:'var(--rv-text-secondary)'}}>
                      <span style={{color:'var(--rv-error)'}}>*</span> Согласие на обработку персональных данных в соответствии с{' '}
                      <button type="button" onClick={openPrivacyPolicy}
                        style={{color:'var(--rv-teal)', background:'none', border:'none', cursor:'pointer', padding:0, textDecoration:'underline'}}>
                        Политикой конфиденциальности
                      </button>
                    </span>
                  </label>

                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      required
                      className="mt-0.5 h-4 w-4 rounded"
                      style={{accentColor:'var(--rv-teal)'}}
                      checked={consentData.termsOfService}
                      onChange={(e) => setConsentData({...consentData, termsOfService: e.target.checked, ageConfirmation: e.target.checked})}
                    />
                    <span className="text-xs leading-relaxed" style={{color:'var(--rv-text-secondary)'}}>
                      <span style={{color:'var(--rv-error)'}}>*</span> Принимаю{' '}
                      <button type="button" onClick={openTermsOfService}
                        style={{color:'var(--rv-teal)', background:'none', border:'none', cursor:'pointer', padding:0, textDecoration:'underline'}}>
                        Условия использования
                      </button>{' '}
                      и подтверждаю, что мне исполнилось <strong>18 лет</strong>
                    </span>
                  </label>

                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      required
                      className="mt-0.5 h-4 w-4 rounded"
                      style={{accentColor:'var(--rv-teal)'}}
                      checked={consentData.privacyPolicy}
                      onChange={(e) => setConsentData({...consentData, privacyPolicy: e.target.checked})}
                    />
                    <span className="text-xs leading-relaxed" style={{color:'var(--rv-text-secondary)'}}>
                      <span style={{color:'var(--rv-error)'}}>*</span> Ознакомлен и согласен с{' '}
                      <button type="button" onClick={openPrivacyPolicy}
                        style={{color:'var(--rv-teal)', background:'none', border:'none', cursor:'pointer', padding:0, textDecoration:'underline'}}>
                        Политикой конфиденциальности
                      </button>
                    </span>
                  </label>

                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded"
                      style={{accentColor:'var(--rv-teal)'}}
                      checked={consentData.marketingEmails}
                      onChange={(e) => setConsentData({...consentData, marketingEmails: e.target.checked})}
                    />
                    <span className="text-xs leading-relaxed" style={{color:'var(--rv-text-secondary)'}}>
                      Согласен получать информационные рассылки (можно отозвать)
                    </span>
                  </label>

                  <p className="text-xs" style={{color:'var(--rv-text-muted)'}}>
                    <span style={{color:'var(--rv-error)'}}>*</span> — обязательные поля (152-ФЗ)
                  </p>
                </div>
              </>
            )}

            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg text-sm font-semibold transition-colors"
              style={{
                background: loading ? 'var(--rv-text-muted)' : 'var(--rv-navy)',
                color: 'white',
                border: 'none',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: 'IBM Plex Sans, sans-serif',
              }}
            >
              {loading ? 'Загрузка...' : (isLogin ? 'Войти' : 'Зарегистрироваться')}
            </button>
          </form>

          <div className="mt-5 text-center space-y-2">
            {isLogin && (
              <button
                data-testid="auth-forgot-password-btn"
                onClick={() => setShowPasswordRecovery(true)}
                className="text-sm"
                style={{color:'var(--rv-text-secondary)', background:'none', border:'none', cursor:'pointer'}}
              >
                Забыли пароль?
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Модальные окна документов */}
      {showPrivacyModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4"
          style={{background:'rgba(0,0,0,0.5)'}}>
          <div className="bg-white rounded-2xl max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl relative w-full">
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center"
              style={{borderColor:'var(--rv-border)'}}>
              <h2 className="text-lg font-bold" style={{fontFamily:'Manrope,sans-serif'}}>Политика конфиденциальности</h2>
              <button onClick={() => setShowPrivacyModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full text-lg font-bold"
                style={{background:'var(--rv-paper)', border:'none', cursor:'pointer', color:'var(--rv-text-primary)'}}>
                ×
              </button>
            </div>
            <div className="p-6"><PrivacyPolicy /></div>
          </div>
        </div>
      )}

      {showTermsModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4"
          style={{background:'rgba(0,0,0,0.5)'}}>
          <div className="bg-white rounded-2xl max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl relative w-full">
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center"
              style={{borderColor:'var(--rv-border)'}}>
              <h2 className="text-lg font-bold" style={{fontFamily:'Manrope,sans-serif'}}>Условия использования</h2>
              <button onClick={() => setShowTermsModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full text-lg font-bold"
                style={{background:'var(--rv-paper)', border:'none', cursor:'pointer', color:'var(--rv-text-primary)'}}>
                ×
              </button>
            </div>
            <div className="p-6"><TermsOfService /></div>
          </div>
        </div>
      )}
    </div>
    <LeadForm page="/" />
    <SiteFooter />
    </>
  );
};
